// Low-level WebAssembly encoding primitives.
//
// We hand-roll the WASM binary format rather than depending on a toolkit
// because (a) the encoded format is small and well-specified, (b) the
// codegen pass needs fine control over the byte stream, and (c) avoiding a
// dependency keeps the build chain free of WAT-to-WASM tooling.
export class ByteWriter {
    chunks = [];
    bytes(b) {
        for (let i = 0; i < b.length; i++)
            this.chunks.push(b[i] & 0xff);
    }
    byte(b) { this.chunks.push(b & 0xff); }
    // Unsigned LEB128 — sizes, indices.
    uleb128(value) {
        let v = typeof value === "bigint" ? value : BigInt(value);
        if (v < 0n)
            throw new Error(`uleb128: negative value ${v}`);
        while (true) {
            const byte = Number(v & 0x7fn);
            v >>= 7n;
            if (v === 0n) {
                this.byte(byte);
                return;
            }
            this.byte(byte | 0x80);
        }
    }
    // Signed LEB128 — i32/i64 constants.
    sleb128(value) {
        let v = typeof value === "bigint" ? value : BigInt(value);
        while (true) {
            const byte = Number(v & 0x7fn);
            const signBit = (byte & 0x40) !== 0;
            v >>= 7n;
            // Arithmetic right shift on bigint: BigInt's >> is signed-shift for
            // negative values, so this is fine.
            if ((v === 0n && !signBit) || (v === -1n && signBit)) {
                this.byte(byte);
                return;
            }
            this.byte(byte | 0x80);
        }
    }
    // Length-prefixed UTF-8 string (WASM "name" type).
    name(s) {
        const enc = new TextEncoder().encode(s);
        this.uleb128(enc.length);
        this.bytes(enc);
    }
    // Uint8Array.from always allocates a fresh ArrayBuffer — keep that in
    // the type so compiler output satisfies BufferSource without casts.
    toUint8Array() {
        return Uint8Array.from(this.chunks);
    }
    length() { return this.chunks.length; }
}
// Helper: produce a "section" — id byte + size-prefixed contents.
export function makeSection(id, contentBuilder) {
    const inner = new ByteWriter();
    contentBuilder(inner);
    const out = new ByteWriter();
    out.byte(id);
    out.uleb128(inner.length());
    out.bytes(inner.toUint8Array());
    return out.toUint8Array();
}
// ----- WASM value/section enums -----
export const ValType = {
    i32: 0x7f,
    i64: 0x7e,
    f32: 0x7d,
    f64: 0x7c,
};
export const BlockType = {
    empty: 0x40,
    i32: ValType.i32,
};
export const SectionId = {
    Type: 1, Import: 2, Function: 3, Table: 4, Memory: 5,
    Global: 6, Export: 7, Start: 8, Element: 9, Code: 10, Data: 11,
};
export const ExportKind = {
    Func: 0x00, Table: 0x01, Mem: 0x02, Global: 0x03,
};
export const ImportKind = {
    Func: 0x00, Table: 0x01, Mem: 0x02, Global: 0x03,
};
// ----- opcodes (subset we use) -----
export const Op = {
    // control
    unreachable: 0x00,
    nop: 0x01,
    block: 0x02,
    loop: 0x03,
    if_: 0x04,
    else_: 0x05,
    end: 0x0b,
    br: 0x0c,
    br_if: 0x0d,
    return_: 0x0f,
    call: 0x10,
    // parametric
    drop: 0x1a,
    select: 0x1b,
    // variable
    local_get: 0x20,
    local_set: 0x21,
    local_tee: 0x22,
    global_get: 0x23,
    global_set: 0x24,
    // memory
    i32_load: 0x28,
    i32_load8_s: 0x2c,
    i32_load8_u: 0x2d,
    i32_load16_s: 0x2e,
    i32_load16_u: 0x2f,
    i32_store: 0x36,
    i32_store8: 0x3a,
    i32_store16: 0x3b,
    memory_size: 0x3f,
    memory_grow: 0x40,
    // numeric constants
    i32_const: 0x41,
    // i32 numeric
    i32_eqz: 0x45,
    i32_eq: 0x46,
    i32_ne: 0x47,
    i32_lt_s: 0x48,
    i32_lt_u: 0x49,
    i32_gt_s: 0x4a,
    i32_gt_u: 0x4b,
    i32_le_s: 0x4c,
    i32_le_u: 0x4d,
    i32_ge_s: 0x4e,
    i32_ge_u: 0x4f,
    i32_add: 0x6a,
    i32_sub: 0x6b,
    i32_mul: 0x6c,
    i32_div_s: 0x6d,
    i32_div_u: 0x6e,
    i32_rem_s: 0x6f,
    i32_rem_u: 0x70,
    i32_and: 0x71,
    i32_or: 0x72,
    i32_xor: 0x73,
    i32_shl: 0x74,
    i32_shr_s: 0x75,
    i32_shr_u: 0x76,
};
//# sourceMappingURL=encoding.js.map